import {
  AccountType,
  AddOnRateType,
  AddOnType,
  deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  patchV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  postV1CompaniesCompanyIdOrdersOrderIdAutoAddOnAssignments,
} from '@treadinc/horizon-api-spec';

import { Order } from '~hooks/useOrders/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';

/**
 * Detect the backend uniqueness 422 ("Rate is already taken") raised when an
 * add-on assignment duplicates one already on the order — i.e. the same
 * (assignmentable, account_type, add_on_id, percentage, quantity, rate,
 * add_on_rate_type, cost_rate_value) tuple. Lets the order form surface a
 * clear, actionable message instead of the opaque per-batch failure count.
 */
export function isDuplicateAddOnAssignmentError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (Number(response?.status) !== 422) return false;
  const data = response?.data as
    | { error?: { message?: string; errors?: Array<{ message?: string }> } }
    | undefined;
  const messages = [
    data?.error?.message ?? '',
    ...(data?.error?.errors ?? []).map((entry) => entry?.message ?? ''),
  ].join(' ');
  return /already\s+(been\s+)?taken/i.test(messages);
}

/**
 * True when any settled add-on batch rejected with the duplicate-add-on
 * uniqueness 422. Lets the order form decide whether to show the actionable
 * "already on the order" message.
 */
export function hasDuplicateAddOnAssignmentFailure(
  results: PromiseSettledResult<unknown>[],
): boolean {
  return results.some(
    (result) =>
      result.status === 'rejected' && isDuplicateAddOnAssignmentError(result.reason),
  );
}

interface ProcessAddOnAssignmentsForOrderArgs {
  companyId: string;
  order: Order;
  addOnAssignments: AddOnAssignmentSchema[];
  accountType?: AccountType;
}

/**
 * Sync a batch of add-on assignment edits to the API for a given order.
 *
 * Runs as three sequential phases: DELETE, then PATCH (update), then POST
 * (create). Strict phase ordering means a cascade-replacement POST carrying
 * `original_auto_add_on_assignment_id` can't race the DELETE of the row it
 * references, and a POST that depends on an updated parent state sees the
 * post-PATCH state on the wire. Each phase uses `Promise.allSettled` so a
 * single rejection doesn't short-circuit other in-flight requests — every
 * queued add-on op gets a chance to land. The first rejection is then
 * rethrown so callers can record the batch as failed.
 */
export async function processAddOnAssignmentsForOrder({
  companyId,
  order,
  addOnAssignments,
  accountType,
}: ProcessAddOnAssignmentsForOrderArgs) {
  // The `.map` bodies eagerly fire requests, so each phase's `.map` must be
  // constructed *after* the prior phase settles — not earlier.
  const deleteResults = await Promise.allSettled(
    addOnAssignments
      .filter(
        (autoAddOn: AddOnAssignmentSchema) => autoAddOn.isDeleted && !autoAddOn.isNew,
      )
      .map((autoAddOn: AddOnAssignmentSchema) =>
        deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId({
          path: { 'company-id': companyId, id: autoAddOn?.addOn?.id ?? '' },
        }),
      ),
  );

  const patchResults = await Promise.allSettled(
    addOnAssignments
      .filter(
        (autoAddOn: AddOnAssignmentSchema) => !autoAddOn.isDeleted && !autoAddOn.isNew,
      )
      .map((autoAddOn: AddOnAssignmentSchema) =>
        patchV1CompaniesCompanyIdAutoAddOnAssignmentsId({
          path: { 'company-id': companyId, id: autoAddOn?.addOn?.id ?? '' },
          body: {
            name: autoAddOn?.addOn?.name ?? '',
            quantity: autoAddOn?.addOn?.quantity ?? 0,
            revenue_rate_value:
              autoAddOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
              autoAddOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                ? autoAddOn?.addOn?.rateValue?.toString()
                : undefined,
            percentage:
              autoAddOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                ? autoAddOn?.addOn?.rateValue
                : undefined,
            add_on_rate_type: autoAddOn?.addOn?.rateType as AddOnRateType,
            add_on_types: (autoAddOn?.addOn?.addOnTypes?.map(
              (addOnType: { id: string; name: string }) => addOnType.id,
            ) ?? []) as AddOnType[],
            cost_rate_value: autoAddOn?.addOn?.costRateValue
              ? String(autoAddOn?.addOn?.costRateValue)
              : undefined,
            total_installments: autoAddOn?.addOn?.totalInstallments,
          },
        }),
      ),
  );

  const postResults = await Promise.allSettled(
    addOnAssignments
      .filter(
        (autoAddOn: AddOnAssignmentSchema) => !autoAddOn.isDeleted && autoAddOn.isNew,
      )
      .map((autoAddOn: AddOnAssignmentSchema) =>
        postV1CompaniesCompanyIdOrdersOrderIdAutoAddOnAssignments({
          path: { 'company-id': companyId, 'order-id': order.id },
          body: {
            account_type: accountType ?? undefined,
            name: autoAddOn?.addOn?.name ?? '',
            quantity: autoAddOn?.addOn?.quantity ?? 0,
            revenue_rate_value:
              autoAddOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
              autoAddOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                ? autoAddOn?.addOn?.rateValue
                : undefined,
            percentage:
              autoAddOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                ? autoAddOn?.addOn?.rateValue
                : undefined,
            add_on_rate_type: autoAddOn?.addOn?.rateType as AddOnRateType,
            external_id: autoAddOn?.addOn?.externalId ?? '',
            accounting_id: autoAddOn?.addOn?.accountingId ?? '',
            add_on_id: autoAddOn?.addOn?.addOnId ?? '',
            add_on_types: (autoAddOn?.addOn?.addOnTypes?.map(
              (addOnType: { id: string; name: string }) => addOnType.id,
            ) ?? []) as AddOnType[],
            total_installments: autoAddOn?.addOn?.totalInstallments ?? 0,
            cost_rate_value: autoAddOn?.addOn?.costRateValue ?? undefined,
            original_auto_add_on_assignment_id:
              autoAddOn?.addOn?.originalAutoAddOnAssignmentId ?? null,
          },
        }),
      ),
  );

  // Surface any rejection to the caller so the outer `Promise.allSettled` at
  // the call site keeps recording the batch as failed in `addOnsFailedCount`.
  // Without this rethrow, partial failures would be silently swallowed.
  const firstRejection = [...deleteResults, ...patchResults, ...postResults].find(
    (r): r is PromiseRejectedResult => r.status === 'rejected',
  );
  if (firstRejection) {
    throw firstRejection.reason;
  }
  return [...patchResults, ...postResults].map(
    (r) => (r as PromiseFulfilledResult<unknown>).value,
  );
}
